import React, { Fragment, useMemo } from "react";
import Carousel from "react-multi-carousel";
import FareLeg from "./Fare.Leg";
import { AvailableFareModel } from "WebApiClient";
import { FareFilterApplicability } from "./FareFilter.Applicability";
import { AvailableFaresFare } from "components/AvailableFares/component/AvailableFares.Fares";

interface FareLegListProps {
    FareData: AvailableFareModel;
    internalSelectedConnections: number[];
    showFlightTimes: boolean;
    SetSelectedConnection: (legIndex: number, connIndex: number) => void;
    DistinctPlatingCarriers: any[];
    Applicability?: FareFilterApplicability;
    isActivate?: boolean;
    ToggleFlightTimes: () => void;
    setStatus: (v: boolean) => void;
    upSaleToggle: boolean;
    upscaleFilterData: AvailableFaresFare[];
    GetSelectedConnectionIndexByLegIndex: (legIndex: number) => number;
    GetSelectedConnectionIndexByLegIndexUpsell: (fareData: any, legIndex: number) => number;
    responsive: any;
    visibleItems: number;
    scrollToBook: (fareIndex: number) => void;
}

const FareLegList: React.FC<FareLegListProps> = ({
    FareData,
    internalSelectedConnections,
    showFlightTimes,
    SetSelectedConnection,
    DistinctPlatingCarriers,
    Applicability,
    isActivate,
    ToggleFlightTimes,
    setStatus,
    upSaleToggle,
    upscaleFilterData,
    GetSelectedConnectionIndexByLegIndex,
    GetSelectedConnectionIndexByLegIndexUpsell,
    responsive,
    visibleItems,
    scrollToBook,
}) => {
    // 🔧 Helper function: Upsell cards for each leg index
    const getUpsellCardsForLeg = useMemo(() => {
        const map: Record<number, { leg: any; fare: any }[]> = {};

        upscaleFilterData.forEach(fareWrapper => {
            fareWrapper.Fare.legs.forEach(leg => {
                const connIndex = GetSelectedConnectionIndexByLegIndexUpsell(fareWrapper, leg.index);
                const connection = leg.connections.find(c => c.index === connIndex);
                if (connection) {
                    if (!map[leg.index]) map[leg.index] = [];
                    map[leg.index].push({ leg, fare: fareWrapper.Fare });
                }
            });
        });

        return map;
    }, [upscaleFilterData, GetSelectedConnectionIndexByLegIndexUpsell]);

    return (
        <>
            {FareData.legs.map((leg) => {
                const legIndex = leg.index;
                if (internalSelectedConnections[legIndex] === undefined) return null;
                const upsellCardsForThisLeg = getUpsellCardsForLeg[legIndex] ?? [];
                return (
                    <Fragment key={`LegWrapper-${legIndex}`}>
                        <FareLeg
                            LegIndex={legIndex}
                            SelectedConnection={GetSelectedConnectionIndexByLegIndex(legIndex)}
                            GetSelectedConnectionIndexByLegIndex={GetSelectedConnectionIndexByLegIndex}
                            Data={leg}
                            isActivate={isActivate}
                            ShowFlightTimes={showFlightTimes}
                            SelectConnection={SetSelectedConnection}
                            ShowLegPlatingCarrier={DistinctPlatingCarriers.length > 1}
                            ApplicableConnections={Applicability?.ApplicableConnections?.[legIndex]}
                            Fare={FareData}
                            ToggleFlightTimes={ToggleFlightTimes}
                            setStatus={setStatus}
                            headerOffVisible={true}
                            upscaleLength={upsellCardsForThisLeg.length}
                        />

                        {upSaleToggle && upsellCardsForThisLeg.length > 0 && (
                            <div className="upSellCardParent">
                                <Carousel
                                    responsive={responsive}
                                    slidesToSlide={5}
                                    swipeable
                                    className="carouselUpsell"
                                    arrows={upsellCardsForThisLeg.length > visibleItems}
                                >
                                    {upsellCardsForThisLeg.map(({ leg: legU, fare }, idx) => (
                                        <div
                                            className="upSellContainer"
                                            key={`Upsell-${fare.fareIndex}-${legU.index}-${idx}`}
                                            onClick={() => scrollToBook(fare.fareIndex)}
                                        >
                                            <FareLeg
                                                LegIndex={legU.index}
                                                SelectedConnection={GetSelectedConnectionIndexByLegIndexUpsell(
                                                    { Fare: fare }, legU.index
                                                )}
                                                GetSelectedConnectionIndexByLegIndex={GetSelectedConnectionIndexByLegIndex}
                                                Data={legU}
                                                isActivate={isActivate}
                                                ShowFlightTimes={showFlightTimes}
                                                SelectConnection={SetSelectedConnection}
                                                ShowLegPlatingCarrier={DistinctPlatingCarriers.length > 1}
                                                ApplicableConnections={Applicability?.ApplicableConnections?.[legU.index]}
                                                Fare={fare}
                                                ToggleFlightTimes={ToggleFlightTimes}
                                                setStatus={setStatus}
                                                headerOffVisible={false}
                                                upscaleLength={upsellCardsForThisLeg.length}
                                            />
                                        </div>
                                    ))}
                                </Carousel>
                            </div>
                        )}
                    </Fragment>
                );
            })}
        </>
    );
};

export default FareLegList;
